/**
 * One student's progress — the subject trend, the papers, and the sheet.
 *
 * TWO surfaces render this and they must not diverge: the student's own
 * Progress tab, and the teacher's per-student view. A teacher opening a child's
 * report has to be looking at the page the child is looking at, or the PTM is
 * two people reading different documents. So it is one component, and the
 * teacher's copy differs in exactly two ways, both of them honest:
 *
 *   - whose name is on it (`possessive`);
 *   - which tutor the sheet offers (`viewer`) — the per-question tutor answers
 *     only the student whose Pink card it is.
 *
 * Which subjects a teacher may open is decided by the server, which sends only
 * those — a filter here would only hide what had already been sent. The sheet
 * is NOT reordered for teachers: band-reversed, it would quietly have become a
 * different document from the child's.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from 'recharts';
import { apiGet } from '@/lib/api';
import { BIN_LABEL, BIN_ORDER, binRamp } from '@/lib/bins';
import { MasterySheet } from '@/components/reports/MasterySheet';
import type { BinColor, MasterySheetOption } from '@cwy/config/contracts';
import { fontPx } from '@/lib/fontSize';

const MODE_LABEL: Record<string, string> = {
  EXAM: 'Exam',
  MINI_TEST: 'Mini test',
  PRACTICE: 'Practice',
};

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/** The five bands of one paper, as a single proportional bar. */
function RampBar({ counts }: { counts: Record<BinColor, number> }) {
  const total = BIN_ORDER.reduce((s, b) => s + counts[b], 0) || 1;
  return (
    <div
      className="flex h-1.5 w-full overflow-hidden rounded-full"
      style={{ background: 'var(--track)' }}
      role="img"
      aria-label={BIN_ORDER.map((b) => `${BIN_LABEL[b]} ${counts[b]}`).join(', ')}
    >
      {BIN_ORDER.filter((b) => counts[b] > 0).map((b) => (
        <div
          key={b}
          style={{ width: `${(counts[b] / total) * 100}%`, background: binRamp(b).border }}
        />
      ))}
    </div>
  );
}

/**
 * The run of papers as a line.
 *
 * Oldest on the left, so the line reads the way a term does. Every point is the
 * same paper the card under it opens — clicking one picks it — because a trend
 * a student cannot open is a number they can only feel bad about.
 *
 * Compete With Yourself: the only comparison drawn here is against this
 * student's own earlier papers. There is no class line and no cohort band.
 */
function ScoreTrend({
  papers,
  subject,
  selected,
  onPick,
}: {
  papers: MasterySheetOption[];
  subject: string;
  selected?: string;
  onPick: (studentPaperId: string) => void;
}) {
  // The API hands back newest first; a trend has to run the other way.
  const data = useMemo(() => {
    const ordered = [...papers].reverse();
    return ordered.map((p, i) => {
      const label = fmtDate(p.date);
      return {
        studentPaperId: p.studentPaperId,
        label,
        // Several papers can be set on one day, and a dozen identical ticks
        // reads as a broken axis. Print the date when it changes and leave the
        // repeats blank — the points still sit in order underneath.
        tick: i > 0 && fmtDate(ordered[i - 1].date) === label ? '' : label,
        percentage: p.percentage,
        title: p.title,
      };
    });
  }, [papers]);

  // One point is not a trend, and a lone dot on an empty grid says less than the
  // card below it already does.
  if (data.length < 2) return null;

  return (
    <section aria-label={`${subject} scores across papers`}>
      {/* One subject at a time, so the title names the line and no legend is
          needed. Subject hues would also have to come from somewhere, and the
          five band colours are spoken for — a Green line would read as mastered. */}
      <h2 className="mb-2 text-sm font-bold" style={{ color: 'var(--text-body)' }}>
        {subject} — scores, paper by paper
      </h2>
      <div
        className="rounded-2xl px-2 py-3"
        style={{ background: 'var(--surface-card)', border: '1px solid var(--border-default)' }}
      >
        <div className="h-44">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 18, left: 4, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" vertical={false} />
              <XAxis
                dataKey="tick"
                interval={0}
                tick={{ style: { fontSize: fontPx(11) }, fill: 'var(--ink-3)' }}
                stroke="var(--border-default)"
                tickLine={false}
              />
              <YAxis
                domain={[0, 100]}
                ticks={[0, 25, 50, 75, 100]}
                tickFormatter={(v: number) => `${v}%`}
                tick={{ style: { fontSize: fontPx(10) }, fill: 'var(--ink-3)' }}
                stroke="var(--border-default)"
                tickLine={false}
                width={46}
              />
              <Tooltip content={<TrendTooltip />} cursor={{ stroke: 'var(--border-strong)' }} />
              <Line
                type="monotone"
                dataKey="percentage"
                stroke="var(--brand-forest)"
                strokeWidth={2}
                isAnimationActive={false}
                dot={(props) => {
                  const point = data[props.index as number];
                  const isSelected = point.studentPaperId === selected;
                  return (
                    <circle
                      key={point.studentPaperId}
                      cx={props.cx}
                      cy={props.cy}
                      r={isSelected ? 6 : 4}
                      fill={isSelected ? 'var(--brand-forest)' : 'var(--surface-card)'}
                      stroke="var(--brand-forest)"
                      strokeWidth={2}
                      style={{ cursor: 'pointer' }}
                      onClick={() => onPick(point.studentPaperId)}
                    />
                  );
                }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  );
}

function TrendTooltip({ active, payload }: TooltipProps<number, string>) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload as { title: string; label: string; percentage: number };
  return (
    <div
      className="rounded-lg px-3 py-2 text-xs"
      style={{ background: 'var(--brand-forest)', color: 'var(--text-on-forest)' }}
    >
      <p className="font-bold">{point.title}</p>
      <p data-numeric="" className="opacity-90">
        {point.label} · {point.percentage}%
      </p>
    </div>
  );
}

export function StudentProgress({
  studentId,
  possessive = 'Your',
  viewer = 'student',
  emptyNote = 'Sit a paper and your sheet appears here.',
}: {
  studentId: string;
  /** "Your" on the student's own page, "Ishaan's" on a teacher's. */
  possessive?: string;
  viewer?: 'student' | 'teacher';
  emptyNote?: string;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const [pickedSubject, setPickedSubject] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['mastery-sheets', studentId],
    enabled: Boolean(studentId),
    queryFn: () => apiGet<MasterySheetOption[]>(`/students/${studentId}/mastery-sheets`),
  });

  const papers = useMemo(() => data ?? [], [data]);

  // Scores only mean something inside one subject: a Maths paper and an English
  // paper are not two points on the same trend, and joining them draws a line
  // out of the timetable rather than out of the work.
  const subjects = useMemo(() => [...new Set(papers.map((p) => p.subject))].sort(), [papers]);
  const subject = pickedSubject && subjects.includes(pickedSubject) ? pickedSubject : subjects[0];
  const subjectPapers = useMemo(
    () => papers.filter((p) => p.subject === subject),
    [papers, subject],
  );

  // Newest paper until another is picked; `picked` can outlive a refetch or a
  // change of subject, so fall back rather than trusting it to still be in the
  // list. That fallback is also what re-points the sheet when the subject
  // changes, which is why no effect is needed to reset it.
  const selected =
    subjectPapers.find((p) => p.studentPaperId === picked)?.studentPaperId ??
    subjectPapers[0]?.studentPaperId;

  if (isLoading) {
    return (
      <div
        className="h-24 animate-pulse rounded-2xl"
        style={{ background: 'var(--surface-sunk)' }}
      />
    );
  }

  if (isError) {
    return (
      <p className="p-6 text-center" style={{ color: 'var(--ink-3)' }}>
        Couldn't load these papers.
      </p>
    );
  }

  if (papers.length === 0) {
    return (
      <div
        className="rounded-2xl border border-dashed p-10 text-center"
        style={{ borderColor: 'var(--border-strong)', background: 'var(--surface-card)' }}
      >
        <p className="font-bold" style={{ color: 'var(--text-body)' }}>
          No papers yet
        </p>
        <p className="mt-1 text-sm" style={{ color: 'var(--ink-3)' }}>
          {emptyNote}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {subjects.length > 1 && (
        <div
          className="flex gap-1.5 overflow-x-auto rounded-full p-1"
          style={{ background: 'var(--paper-sunk)' }}
          role="tablist"
          aria-label="Which subject"
        >
          {subjects.map((name) => {
            const active = name === subject;
            return (
              <button
                key={name}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setPickedSubject(name)}
                className="shrink-0 rounded-full px-3.5 py-1.5 text-xs font-bold transition"
                style={
                  active
                    ? { background: 'var(--slab)', color: 'var(--text-on-forest)' }
                    : { color: 'var(--ink-2)' }
                }
              >
                {name}
              </button>
            );
          })}
        </div>
      )}

      <ScoreTrend papers={subjectPapers} subject={subject} selected={selected} onPick={setPicked} />

      {/* The picker. Left to right is oldest-last, so the run of percentages
          reads as the trend the line chart draws above it. */}
      <section aria-label="Choose a paper">
        <h2 className="mb-2 text-sm font-bold" style={{ color: 'var(--text-body)' }}>
          {possessive} {subject} papers
        </h2>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {subjectPapers.map((p) => {
            const active = p.studentPaperId === selected;
            return (
              <button
                key={p.studentPaperId}
                type="button"
                onClick={() => setPicked(p.studentPaperId)}
                aria-pressed={active}
                className="flex w-44 flex-none flex-col gap-1.5 rounded-xl px-3 py-2.5 text-left transition"
                style={{
                  background: active ? 'var(--brand-lime-soft)' : 'var(--surface-card)',
                  border: `1px solid ${active ? 'var(--brand-ink)' : 'var(--border-default)'}`,
                  boxShadow: active ? 'var(--shadow-sm)' : undefined,
                }}
              >
                <span className="flex items-baseline justify-between gap-2">
                  <span
                    className="truncate text-fs-11 font-bold uppercase tracking-wide"
                    style={{ color: 'var(--ink-3)' }}
                  >
                    {p.subject}
                  </span>
                  <span
                    data-numeric=""
                    className="flex-none text-sm font-extrabold"
                    style={{ color: 'var(--text-strong)' }}
                  >
                    {p.percentage}%
                  </span>
                </span>

                <span
                  className="line-clamp-2 text-fs-12 font-semibold leading-snug"
                  style={{ color: 'var(--text-body)' }}
                >
                  {p.title}
                </span>

                <RampBar counts={p.binCounts} />

                <span className="text-fs-10 font-semibold" style={{ color: 'var(--ink-3)' }}>
                  {MODE_LABEL[p.mode] ?? p.mode} · {fmtDate(p.date)}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {selected && <MasterySheet spId={selected} viewer={viewer} />}
    </div>
  );
}
